import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { applyLocale, browserLocale, isSupportedLocale, type SupportedLocale } from '@/i18n';
import { setTelemetryConsent } from '@/telemetry/appInsights';
import { setAnalyticsConsent as setGaConsent } from '@/telemetry/analytics';

const STORAGE_KEY = 'ck.preferences';
/** Vue 2 portal persisted everything (incl. customer PII) under this key. */
const LEGACY_VUEX_KEY = 'vuex';

interface StoredPreferences {
  language?: string;
  analyticsConsent?: boolean;
}

function readStorage(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * One-time migration from the Vue 2 portal: keep only language + analytics consent
 * and delete the old blob (it contained customer profile data and Nexi payloads).
 */
function readInitialPreferences(): StoredPreferences {
  const current = readStorage(STORAGE_KEY) as StoredPreferences | null;
  if (current) return current;

  const legacy = readStorage(LEGACY_VUEX_KEY) as { common?: StoredPreferences } | null;
  try {
    localStorage.removeItem(LEGACY_VUEX_KEY);
  } catch {
    /* ignore */
  }
  return {
    language: legacy?.common?.language,
    analyticsConsent: legacy?.common?.analyticsConsent,
  };
}

/** Small, non-sensitive, per-device preferences. Persisted in localStorage. */
export const usePreferencesStore = defineStore('preferences', () => {
  const initial = readInitialPreferences();

  const language = ref<SupportedLocale>(isSupportedLocale(initial.language) ? initial.language : browserLocale());
  const analyticsConsent = ref<boolean>(initial.analyticsConsent === true);

  applyLocale(language.value);

  watch(
    [language, analyticsConsent],
    () => {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ language: language.value, analyticsConsent: analyticsConsent.value }),
        );
      } catch {
        /* storage unavailable */
      }
    },
    { immediate: true },
  );

  function setLanguage(locale: SupportedLocale): void {
    language.value = locale;
    applyLocale(locale);
  }

  function setAnalyticsConsent(granted: boolean): void {
    analyticsConsent.value = granted;
    setTelemetryConsent(granted);
    setGaConsent(granted);
  }

  return { language, analyticsConsent, setLanguage, setAnalyticsConsent };
});
